import { Link } from "react-router-dom";
import AccordionItem from "./components/AccordionItem";
import AccordionSection from "./components/AccordionSection";
import SectionCard from "./components/SectionCard";
import Footer from "./components/Footer";

const popularQuestions = [
  {
    question: "Step-by-step guide to cancelling",
    answer:
      "You can cancel your subscription at any time by reaching out to our support team. Simply send us an email, and we&apos;ll help you with the cancellation process as quickly as possible. No forms, no complicated steps — we&apos;ll handle everything for you.",
  },
  {
    question: "Where is my refund?",
    answer:
      "Refunds typically take 14 business days to appear in your account after processing. If you haven&apos;t received your refund within this timeframe, please contact our support team with your transaction details.",
  },
  {
    question: "I don't recognize this charge",
    answer:
      "If you don&apos;t recognize a charge, please check your email for receipts and transaction confirmations. The charge may appear under our company name &quot;Kingcraft Technologies LTD&quot;. If you still don&apos;t recognize it, please contact our support team immediately.",
  },
];

const subscriptionItems = [
  {
    question: "Cancelling my subscription",
    answer: "You can cancel your subscription at any time by contacting our support team. Send an email to support@kingcraftech.com with your account email and reason for cancellation. Our team will process your request within 1–2 business days and send a confirmation. No further charges will be made after cancellation.",
    linkPath: "/subscription/cancelling",
    linkLabel: "Full cancellation guide",
  },
  {
    question: "Does my subscription automatically renew?",
    answer: "Yes, subscriptions renew automatically at the end of each billing cycle unless you cancel before the renewal date. You will be charged the same amount as your original subscription and a receipt will be sent to your registered email. Contact us at least 24 hours before your next billing date to stop renewal.",
    linkPath: "/subscription/auto-renew",
    linkLabel: "Learn about auto-renewal",
  },
  {
    question: "What if I never received the product or service?",
    answer: "We're sorry to hear that. First, check your spam/junk folder for any delivery emails and verify the email address used during sign-up. If you still haven't received it, contact our support team with your order details. We will investigate within 1–2 business days and either re-deliver or issue a full refund.",
    linkPath: "/subscription/no-product",
    linkLabel: "View full details",
  },
];

const billingItems = [
  {
    question: "Where is my refund?",
    answer: "Refunds typically take 14 business days to appear in your account. Processing takes 1–3 business days, then 5–10 business days for your bank to process. Check your statement for a credit from 'Kingcraft Technologies LTD'. If 14 business days have passed, contact us with your transaction ID.",
    linkPath: "/billing/refund",
    linkLabel: "Refund timeline details",
  },
  {
    question: "I don't recognize this charge — what should I do?",
    answer: "The charge may appear as 'Kingcraft Technologies LTD' or 'kingcraftech.com' on your statement. Check your email inbox (including spam) for a receipt from us, and ask family members who may have access to your card. If you still don't recognize it, contact us immediately — we'll investigate within 1–2 business days.",
    linkPath: "/billing/unrecognized-charge",
    linkLabel: "What to do next",
  },
  {
    question: "I was unaware of the charges",
    answer: "This often happens when a free trial converts to a paid subscription or an annual plan renews automatically. Search your email for sign-up confirmations from kingcraftech.com. Contact us with the charge date and amount — we'll trace the origin. If you didn't authorize it, we will process a refund and cancel any active subscription.",
    linkPath: "/billing/unaware-charges",
    linkLabel: "Resolve unexpected charges",
  },
];

export default function Home() {
  return (
    <div className="min-h-screen font-sans bg-[#f4f5f7]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-[#2d2f36] rounded-lg">
                <i className="ri-add-line text-white text-lg font-bold"></i>
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900 leading-tight">Kingcraft Technologies</h1>
                <p className="text-xs text-gray-400 leading-tight">Help Center</p>
              </div>
            </div>
            <a
              href="mailto:support@kingcraftech.com"
              className="hidden sm:flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-sm"></i>
              </div>
              <span className="font-medium">support@kingcraftech.com</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero — light */}
      <div className="bg-white border-b border-gray-200 py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">How can we help you?</h2>
          <p className="text-gray-500 text-sm md:text-base max-w-lg mx-auto">
            Find answers to your questions about subscriptions, billing, and account management.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-10">

        {/* Connect with Support */}
        <SectionCard
          icon="support"
          title="CONNECT WITH SUPPORT"
          description="Need more help? Contact our support team directly."
          links={[
            { label: "Contact Support", path: "/contact-support" },
            { label: "Dispute Resolution Form", path: "/dispute-resolution" },
          ]}
        />

        {/* Popular Questions */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-5">
          <h3 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-widest flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-md">
              <i className="ri-question-answer-line text-gray-500 text-sm"></i>
            </div>
            POPULAR QUESTIONS
          </h3>
          <div>
            {popularQuestions.map((item, idx) => (
              <AccordionItem
                key={idx}
                question={item.question}
                answer={item.answer}
                isLast={idx === popularQuestions.length - 1}
              />
            ))}
          </div>
        </section>

        {/* Subscription Management */}
        <AccordionSection
          icon="subscription"
          title="SUBSCRIPTION MANAGEMENT"
          description="Learn how to manage, cancel or restore your subscription."
          items={subscriptionItems}
        />

        {/* Billing & Payments */}
        <AccordionSection
          icon="billing"
          title="BILLING & PAYMENTS"
          description="Get help with charges, refunds, or payment issues."
          items={billingItems}
        />

        {/* CTA */}
        <section className="bg-[#2d2f36] rounded-2xl p-8 mb-5 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-4">
            <i className="ri-file-list-3-line text-white text-xl"></i>
          </div>
          <h3 className="text-base font-semibold text-white mb-2">
            Didn&apos;t find what you were looking for?
          </h3>
          <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">
            Let&apos;s fix it together. Submit a dispute form and our team will review your case within 2–3 business days.
          </p>
          <Link
            to="/dispute-resolution"
            className="inline-block bg-white text-[#2d2f36] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            Submit a Dispute Form
          </Link>
        </section>

        {/* Footer */}
        <Footer />
      </main>
    </div>
  );
}
